"use client";

import type React from "react";
import { useState, useEffect } from "react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { FreeSubscription } from "@/common/model/FreeSubscription";
import { Input, Label, UserSearchInput } from "@/components/ui";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { User } from "@/common/model/User";
import { formatDateForInput } from "@/helpers/date.helper";
import { ENDPOINTS } from "@/constants/api";

type FreeSubscriptionModalProps = {
  isOpen: boolean;
  subscription: FreeSubscription | null;
  onClose: () => void;
  onSave: (subscription: Partial<FreeSubscription>) => void;
};

export function FreeSubscriptionModal({
  isOpen,
  subscription,
  onClose,
  onSave,
}: FreeSubscriptionModalProps) {
  const [formData, setFormData] = useState<Partial<FreeSubscription>>(
    {} as FreeSubscription
  );
  const [searchedUser, setSearchedUser] = useState<User | null>(null);

  useEffect(() => {
    // Ensure dates are properly set with time at noon to avoid timezone issues
    const setDateWithNoon = (date: Date | string | undefined | null): Date => {
      const newDate = date ? new Date(date) : new Date();
      newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      return newDate;
    };

    setFormData((prev) => ({
      ...prev,
      startDate: setDateWithNoon(subscription?.startDate),
      endDate: setDateWithNoon(subscription?.endDate),
      userId: subscription?.userId || undefined,
      subscriptionType: subscription?.subscriptionType || "",
    }));
    setSearchedUser(null);
  }, [subscription, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.includes("Date") && value) {
      // Create date at noon to avoid timezone issues
      const date = new Date(value);
      date.setHours(12, 0, 0, 0);

      setFormData((prev) => ({
        ...prev,
        [name]: date,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleUserSelect = (selectedUser: User) => {
    setFormData((prev) => ({
      ...prev,
      userId: selectedUser.id,
    }));
    setSearchedUser(selectedUser);
  };

  // Handle clearing the selected user
  const handleClearUser = () => {
    setSearchedUser(null);
    setFormData((prev) => ({
      ...prev,
      userId: undefined,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that a user is selected when creating a new subscription
    if (!subscription?.id && !formData.userId) {
      alert("Por favor, selecciona un usuario para la suscripción");
      return;
    }

    if (!formData.subscriptionType) {
      alert("Please select a subscription tier (Plus or Pro).");
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-backgroundcolor text-white">
        <DialogHeader>
          <DialogTitle>
            {subscription ? "Edit Subscription" : "Add Subscription"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input type="hidden" name="userId" value={formData.userId} />
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                name="startDate"
                type="date"
                value={formatDateForInput(formData?.startDate)}
                onChange={handleChange}
                className="bg-backgroundbox text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={formatDateForInput(formData?.endDate)}
                onChange={handleChange}
                className="bg-backgroundbox text-white"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subscriptionType">Tier</Label>
            <select
              id="subscriptionType"
              name="subscriptionType"
              value={formData.subscriptionType || ""}
              onChange={handleChange}
              required
              className="bg-backgroundbox text-white block w-full rounded-md border border-white25 px-3 py-2 shadow-sm focus:border-lightblue focus:outline-none"
            >
              <option value="" disabled>
                Select a tier
              </option>
              <option value="light">Plus</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          {!subscription?.id && !searchedUser && (
            <div className="space-y-2">
              <Label htmlFor="userEmail">User Email</Label>
              <UserSearchInput
                onUserSelect={(selectedUser: any) => {
                  handleUserSelect(selectedUser);
                }}
              />
            </div>
          )}
          {!subscription?.id && searchedUser && (
            <div className="flex items-center p-4 bg-gray-700 rounded-lg">
              <img
                src={
                  searchedUser.profilepicid
                    ? `${ENDPOINTS.IMAGES.PROFILE}/${searchedUser.profilepicid}`
                    : "/default-profilepic.png"
                }
                alt="User Profile"
                className="h-12 w-12 rounded-full object-cover"
              />
              <div className="ml-4 flex-1">
                <span className="text-white text-lg block">
                  {searchedUser.name}
                </span>
                <span className="text-gray-400 text-sm">
                  {searchedUser.username}
                </span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-200 cursor-pointer"
                onClick={handleClearUser}
              >
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="observations">Observations</Label>
            <Input
              type="text"
              id="observations"
              name="observations"
              value={formData.observations || ""}
              onChange={handleChange}
              className="bg-backgroundbox text-white"
            />
          </div>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="text-white75 hover:text-white border-white75 hover:border-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-lightblue hover:bg-lightshadowblue2 text-white"
            disabled={
              !formData.startDate ||
              !formData.endDate ||
              !formData.userId ||
              !formData.subscriptionType
            }
            onClick={handleSubmit}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
